import { useMemo, useState } from "react";
import { Lock, Play, Star } from "lucide-react";
import { Dims, MAX_LEVEL, generateLevel } from "@/game/level";
import { CrystalKind, EnemyKind } from "@/game/types";
import { loadLevelBests, starsFor } from "@/game/storage";
import { t } from "@/game/i18n";

const BALL_INFO: Record<Exclude<CrystalKind, "ball" | "diamond2">, { label: "bone" | "returns1" | "returns2" | "bombBall" | "iceBall"; color: string; ring: string }> = {
  diamond: { label: "bone", color: "#fbbf24", ring: "#7c2d12" },
  bounce1: { label: "returns1", color: "#6ee7b7", ring: "#059669" },
  bounce2: { label: "returns2", color: "#d8b4fe", ring: "#9333ea" },
  bomb: { label: "bombBall", color: "#475569", ring: "#0f172a" },
  ice: { label: "iceBall", color: "#bae6fd", ring: "#0284c7" },
};
const ENEMY_INFO: Record<EnemyKind, { sprite: string }> = {
  scorpion: { sprite: "scorpion" },
  spider: { sprite: "spider" },
  snake: { sprite: "snake" },
  bat: { sprite: "bat_rest" },
  bouncer: { sprite: "" },
};

const Stars = ({ n, size = 14 }: { n: number; size?: number }) => (
  <span className="inline-flex gap-0.5">
    {[0, 1, 2].map((i) => (
      <Star key={i} size={size} className={i < n ? "text-yellow-300" : "text-white/20"} fill={i < n ? "currentColor" : "none"} />
    ))}
  </span>
);

const SpriteIcon = ({ name, frames = 4 }: { name: string; frames?: number }) => (
  <span
    className="inline-block w-7 h-7 rounded-md bg-black/30"
    style={{ backgroundImage: `url(/sprites/${name}.png)`, backgroundSize: `${frames * 100}% 100%`, backgroundPosition: "0 0", backgroundRepeat: "no-repeat" }}
  />
);

export const LevelDetails = ({ level, dims }: { level: number; dims: Dims }) => {
  const data = useMemo(() => generateLevel(level, dims), [level, dims]);
  const balls = new Map<string, number>();
  data.kinds.flat().forEach((k) => { if (k && k !== "ball") balls.set(k, (balls.get(k) ?? 0) + 1); });
  const enemies = new Map<EnemyKind, number>();
  [...data.enemies.map((e) => e.kind), ...data.reserve].forEach((k) => enemies.set(k, (enemies.get(k) ?? 0) + 1));
  const best = loadLevelBests()[level];
  return (
    <div data-testid="level-details" className="mt-4 rounded-2xl border border-white/20 bg-black/25 p-3 text-sm">
      <div className="flex items-center justify-between">
        <span className="font-display font-bold text-yellow-300 text-lg">{t("levelIntro", { n: level })}</span>
        <span data-testid="level-best-score" className="flex items-center gap-2 text-white/80">
          <Stars n={starsFor(best)} /> {best !== undefined ? <span className="font-mono">{best}</span> : <span className="text-white/40">{t("notPlayed")}</span>}
        </span>
      </div>
      <div className="mt-1 text-xs text-white/60" data-testid="level-enemy-summary">{t("onScreenMax")} {data.cap} · {t("total")} {data.enemies.length + data.reserve.length} {t("enemiesWord")}</div>
      <div className="mt-2 flex flex-wrap gap-2" data-testid="level-enemies">
        {[...enemies.entries()].map(([k, n]) => (
          <span key={k} className="flex items-center gap-1.5 rounded-full bg-white/10 pl-1 pr-2.5 py-0.5">
            {ENEMY_INFO[k].sprite ? <SpriteIcon name={ENEMY_INFO[k].sprite} frames={k === "bat" ? 1 : 4} /> : <span className="w-7 h-7 rounded-full" style={{ background: "radial-gradient(circle at 35% 30%, #93c5fd, #1d4ed8)" }} />}
            <span>{t(k)} ×{n}</span>
          </span>
        ))}
      </div>
      <div className="mt-2 flex flex-wrap gap-2" data-testid="level-balls">
        {[...balls.entries()].map(([k, n]) => {
          const info = BALL_INFO[k as keyof typeof BALL_INFO];
          return (
            <span key={k} className="flex items-center gap-1.5 rounded-full bg-white/10 pl-1 pr-2.5 py-0.5">
              <span className="w-5 h-5 rounded-full border-2" style={{ background: `radial-gradient(circle at 35% 30%, #fff, ${info.color})`, borderColor: info.ring }} />
              <span>{t(info.label)} ×{n}</span>
            </span>
          );
        })}
      </div>
    </div>
  );
};

export const LevelGrid = ({ unlocked, dims, onPick }: { unlocked: number; dims: Dims; onPick: (level: number) => void }) => {
  const total = MAX_LEVEL;
  const [selected, setSelected] = useState(Math.min(unlocked, total));
  const bests = loadLevelBests();
  return (
    <>
      <div className="grid grid-cols-6 sm:grid-cols-10 gap-1.5 mt-4 max-h-[38vh] overflow-y-auto pr-1">
        {Array.from({ length: total }, (_, i) => i + 1).map((lv) => (
          <button
            key={lv}
            data-testid={`level-button-${lv}`}
            disabled={lv > unlocked}
            onClick={() => setSelected(lv)}
            className={`level-btn ${lv > unlocked ? "level-btn-locked" : ""} ${lv === selected ? "level-btn-selected" : ""}`}
          >
            {lv > unlocked ? <Lock size={14} /> : (
              <span className="flex flex-col items-center leading-none gap-0.5">
                <span>{lv}</span>
                <Stars n={starsFor(bests[lv])} size={9} />
              </span>
            )}
          </button>
        ))}
      </div>
      <LevelDetails level={selected} dims={dims} />
      <button data-testid="play-level-button" onClick={() => onPick(selected)} className="btn-primary mt-4 w-full flex items-center justify-center gap-2">
        <Play size={18} /> {t("playLevel")} {selected}
      </button>
    </>
  );
};
